import SideIndicator from "assets/icons/admin-view/SideIndicator";
import { defaultCodeTailwindStyle } from "config/default";
import {
  AnimatePresence,
  motion,
  Reorder,
  useDragControls,
} from "framer-motion";
import {
  MdDeleteOutline,
  MdDragIndicator,
  MdDriveFileRenameOutline,
} from "react-icons/md";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { InstructionStep } from "utils/interfaces";

interface InstructionCardProps {
  index: number;
  instruction: InstructionStep;
  deleteCardAction: (index: number) => void;
  editCardAction: (index: number) => void;
  isSelected?: boolean;
}

const InstructionCard: React.FC<InstructionCardProps> = ({
  index,
  instruction,
  deleteCardAction,
  editCardAction,
  isSelected = false,
}) => {
  // Get the drag controls hook
  const dragControls = useDragControls();

  /**
   * Function when pressing delete button
   */
  const handleDeleteCard = (event: React.MouseEvent) => {
    // Prevent to do edit as well
    event.stopPropagation();
    // Perform the delete action
    deleteCardAction(index);
  };

  /**
   * Function when pressing on the card
   */
  const handleEditCard = () => {
    // Perform the delete action
    editCardAction(index);
  };

  return (
    <Reorder.Item
      as="div"
      value={instruction}
      id={
        instruction.creationTimestamp
          ? instruction.creationTimestamp
          : instruction.title + instruction.paragraph
      }
      dragListener={false}
      dragControls={dragControls}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      className={`relative w-full h-fit px-3 py-4 border-2 bg-neutral-50 ${
        isSelected ? "border-neutral-400" : "border-transparent"
      } rounded-lg justify-center items-center gap-2 inline-flex`}
    >
      {/* Side indicator for the card when editing */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute h-fit top-1/2 -translate-y-1/2 -left-3 z-10"
          >
            <SideIndicator className="fill-neutral-400" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Drag handle */}
      <MdDragIndicator
        className="reorder-handle w-6 h-6 fill-neutral-700 cursor-grab shrink-0 select-none"
        onPointerDown={(event) => dragControls.start(event)}
      />

      {/* Text block */}
      <div className="inline-flex flex-col gap-3 flex-grow min-w-20">
        <div className="text-neutral-700 text-sm font-semibold font-sans leading-[17px] select-none">
          {instruction.title}
        </div>
        <div className="max-h-12 text-neutral-700 text-base font-normal font-sans leading-6 line-clamp-2 select-none">
          <Markdown
            className="text-base font-normal font-sans leading-6 line-clamp-2 select-none"
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              code({ node, className, children, ...props }) {
                return (
                  <code className={defaultCodeTailwindStyle} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {instruction.paragraph}
          </Markdown>
        </div>
      </div>

      {/* Button */}
      {!isSelected && (
        <div
          className="p-2 bg-neutral-100 hover:bg-neutral-200/80 ease-in-out duration-100 rounded-full justify-start items-center gap-2 flex cursor-pointer"
          onClick={handleEditCard}
        >
          <MdDriveFileRenameOutline
            className={`w-5 h-5 ${
              isSelected ? "fill-neutral-300" : "fill-neutral-700"
            }`}
          />
        </div>
      )}
      <div
        className="p-2 bg-neutral-100 hover:bg-neutral-200/80 ease-in-out duration-100 rounded-full justify-start items-center gap-2 flex cursor-pointer"
        onClick={handleDeleteCard}
      >
        <MdDeleteOutline
          className={`w-5 h-5 ${
            isSelected ? "fill-neutral-300" : "fill-neutral-700"
          }`}
        />
      </div>
    </Reorder.Item>
  );
};

export default InstructionCard;
